import type { OccasionId } from '../occasion/occasion';

export type DesignThemeId = string;
export type DesignThemeStatus = 'draft' | 'published';
export type DesignThemeBaseThemeKey =
  | 'citrus_evening_editorial'
  | 'garden_romance_classic'
  | 'midnight_monogram_modern'
  | 'romantic_soft'
  | 'classic_formal';
export type DesignThemeStyleVariant =
  | 'editorial'
  | 'romantic'
  | 'modern'
  | 'minimal'
  | 'playful'
  | 'classic'
  | 'formal';
export type DesignThemePresentationTone =
  | 'elegant'
  | 'modern'
  | 'playful'
  | 'classic'
  | 'formal'
  | 'minimal';
export type DesignThemeFontKey =
  | 'fraunces_source_sans'
  | 'playfair_inter'
  | 'crimson_work_sans';
export type DesignThemePreviewSurfaceKey =
  | 'guest_home'
  | 'guest_schedule'
  | 'publishing_invitation'
  | 'welcome_sign'
  | 'guest_home_mobile';

export interface DesignThemeTokens {
  accent: string;
  accentStrong: string;
  accentSoft: string;
  pageBackground: string;
  surfacePrimary: string;
  surfaceSecondary: string;
  sidebarFrom: string;
  sidebarTo: string;
  inkStrong: string;
  inkSoft: string;
  headingFontFamily: string;
  bodyFontFamily: string;
  borderRadius: string;
  shadowStyle: string;
  primaryBrandColor?: string;
  secondaryBrandColor?: string;
  neutralPalette?: string;
  backgroundPalette?: string;
  borderRadiusFamily?: string;
  elevationFamily?: string;
  accentFontFamily?: string;
  fontScale?: string;
  fontWeightMapping?: string;
  lineHeightPreset?: string;
  letterSpacingPreset?: string;
  textTransformPreference?: string;
  spacingScale?: string;
  containerWidthPreference?: string;
  sectionGapScale?: string;
  cardDensityPreference?: string;
  componentSizePreference?: string;
  defaultHeroTreatment?: string;
  imageCornerTreatment?: string;
  imageOverlayStrength?: string;
  backgroundImageDimming?: string;
  gradientUsage?: string;
  textureNoiseUsage?: string;
  animationLevel?: string;
  hoverIntensity?: string;
  transitionDurationProfile?: string;
  reducedMotionBehavior?: string;
  presentationTone?: DesignThemePresentationTone;
}

export interface DesignThemeAssets {
  logoAssetId: string | null;
  markAssetId: string | null;
  heroAssetId: string | null;
  decorativeAssetId: string | null;
}

export interface DesignThemeTemplates {
  invitation: string | null;
  signage: string | null;
  guestWeb: string | null;
}

export interface DesignThemeIdentity {
  internalThemeVersion: number;
  presetSource: DesignThemeBaseThemeKey | null;
  isCustomTheme: boolean;
  migratedFromLegacyStudio: boolean;
  lastUpdatedAt: string;
}

export interface DesignThemeFoundation {
  occasionTitleDefaultStyle: string;
  primaryBrandColor: string;
  secondaryBrandColor: string;
  accentColor: string;
  neutralPalette: string;
  backgroundPalette: string;
  borderRadiusFamily: string;
  elevationFamily: string;
}

export interface DesignThemeTypographySystem {
  headingFontFamily: string;
  bodyFontFamily: string;
  accentDecorativeFontFamily: string;
  fontScale: string;
  fontWeightMapping: string;
  lineHeightPreset: string;
  letterSpacingPreset: string;
  textTransformPreference: string;
}

export interface DesignThemeShapeAndSpacing {
  borderRadiusScale: string;
  spacingScale: string;
  containerWidthPreference: string;
  sectionGapScale: string;
  cardDensityPreference: string;
  componentSizePreference: string;
}

export interface DesignThemeMediaBehavior {
  defaultHeroTreatment: string;
  imageCornerTreatment: string;
  imageOverlayStrength: string;
  backgroundImageDimming: string;
  gradientUsage: string;
  textureNoiseUsage: string;
}

export interface DesignThemeMotionAndInteraction {
  animationLevel: string;
  hoverIntensity: string;
  transitionDurationProfile: string;
  reducedMotionBehavior: string;
}

export interface DesignThemeModel {
  foundation: DesignThemeFoundation;
  typography: DesignThemeTypographySystem;
  shapeAndSpacing: DesignThemeShapeAndSpacing;
  mediaBehavior: DesignThemeMediaBehavior;
  motionAndInteraction: DesignThemeMotionAndInteraction;
  presentationTone: DesignThemePresentationTone;
}

export interface DesignThemeTokenOverrides {
  accent?: string;
  accentStrong?: string;
  surfacePrimary?: string;
  surfaceSecondary?: string;
  sidebarFrom?: string;
  sidebarTo?: string;
  inkStrong?: string;
  inkSoft?: string;
  headingFontFamily?: string;
  bodyFontFamily?: string;
  primaryBrandColor?: string;
  secondaryBrandColor?: string;
  neutralPalette?: string;
  backgroundPalette?: string;
  borderRadiusFamily?: string;
  elevationFamily?: string;
  accentFontFamily?: string;
  fontScale?: string;
  fontWeightMapping?: string;
  lineHeightPreset?: string;
  letterSpacingPreset?: string;
  textTransformPreference?: string;
  spacingScale?: string;
  containerWidthPreference?: string;
  sectionGapScale?: string;
  cardDensityPreference?: string;
  componentSizePreference?: string;
  defaultHeroTreatment?: string;
  imageCornerTreatment?: string;
  imageOverlayStrength?: string;
  backgroundImageDimming?: string;
  gradientUsage?: string;
  textureNoiseUsage?: string;
  animationLevel?: string;
  hoverIntensity?: string;
  transitionDurationProfile?: string;
  reducedMotionBehavior?: string;
  presentationTone?: DesignThemePresentationTone;
}

export interface DesignThemeEventVisualOverrideRecord {
  id: string;
  occasionId: OccasionId;
  eventId: string;
  heroAssetId: string | null;
  eventAccentColor: string | null;
  eventTitleStyleVariant: 'default' | 'editorial' | 'classic' | 'modern';
  eventCoverMediaAssetId: string | null;
  eventSectionArtAssetId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DesignThemeRecord {
  id: DesignThemeId;
  occasionId: OccasionId;
  themeName: string;
  baseThemeKey: DesignThemeBaseThemeKey;
  styleVariant: DesignThemeStyleVariant;
  status: DesignThemeStatus;
  version: number;
  identity: DesignThemeIdentity;
  model: DesignThemeModel;
  tokens: DesignThemeTokens;
  tokenOverrides: DesignThemeTokenOverrides;
  assets: DesignThemeAssets;
  templates: DesignThemeTemplates;
  publishedAt: string | null;
  derivedFromThemeId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GuestThemeAssetReference {
  assetId: string | null;
  url: string | null;
}

export interface GuestTheme {
  occasionId: OccasionId;
  themeId: DesignThemeId;
  themeName: string;
  version: string;
  baseThemeKey: DesignThemeBaseThemeKey;
  styleVariant: DesignThemeStyleVariant;
  presentationTone: DesignThemePresentationTone;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    accentStrong: string;
    accentSoft: string;
    background: string;
    surface: string;
    surfaceSecondary: string;
    text: string;
    mutedText: string;
    sidebarFrom: string;
    sidebarTo: string;
    border: string;
    borderStrong: string;
  };
  typography: {
    headingFontFamily: string;
    bodyFontFamily: string;
    accentFontFamily: string;
    fontScale: string;
    lineHeightPreset: string;
    letterSpacingPreset: string;
    textTransformPreference: string;
  };
  layout: {
    borderRadius: string;
    borderRadiusFamily: string;
    containerWidthPreference: string;
    spacingScale: string;
    sectionGapScale: string;
    cardDensityPreference: string;
    componentSizePreference: string;
  };
  motion: {
    animationLevel: string;
    hoverIntensity: string;
    transitionDurationProfile: string;
    reducedMotionBehavior: string;
  };
  media: {
    defaultHeroTreatment: string;
    imageCornerTreatment: string;
    imageOverlayStrength: string;
    backgroundImageDimming: string;
    gradientUsage: string;
    textureNoiseUsage: string;
  };
  assets: {
    logo: GuestThemeAssetReference;
    mark: GuestThemeAssetReference;
    hero: GuestThemeAssetReference;
    decorative: GuestThemeAssetReference;
  };
  components: {
    button: {
      radius: string;
      shadow: string;
      hoverIntensity: string;
      textTransformPreference: string;
    };
    card: {
      radius: string;
      shadow: string;
      density: string;
      background: string;
      backgroundSecondary: string;
    };
  };
  templates: DesignThemeTemplates;
  tokens: DesignThemeTokens;
}

export interface DesignThemeBasePreset {
  key: DesignThemeBaseThemeKey;
  displayName: string;
  description: string;
  styleVariant: DesignThemeStyleVariant;
  presentationTone: DesignThemePresentationTone;
  fontKey: DesignThemeFontKey;
  templates: DesignThemeTemplates;
  tokens: DesignThemeTokens;
}

export interface DesignThemePreviewSurfaceDefinition {
  key: DesignThemePreviewSurfaceKey;
  displayName: string;
  previewMode: 'live_shell' | 'publishing_html';
  supportedTokens: Array<keyof DesignThemeTokens>;
  supportedAssets: Array<keyof DesignThemeAssets>;
}

export const DESIGN_THEME_FONT_CHOICES: Record<
  DesignThemeFontKey,
  {
    key: DesignThemeFontKey;
    displayName: string;
    headingFontFamily: string;
    bodyFontFamily: string;
    accentFontFamily: string;
  }
> = {
  fraunces_source_sans: {
    key: 'fraunces_source_sans',
    displayName: 'Fraunces + Source Sans',
    headingFontFamily: "'Fraunces Variable', serif",
    bodyFontFamily: "'Source Sans 3', 'Inter', sans-serif",
    accentFontFamily: "'Fraunces Variable', serif",
  },
  playfair_inter: {
    key: 'playfair_inter',
    displayName: 'Playfair + Inter',
    headingFontFamily: "'Playfair Display', 'Fraunces Variable', serif",
    bodyFontFamily: "'Inter', 'Source Sans 3', sans-serif",
    accentFontFamily: "'Playfair Display', serif",
  },
  crimson_work_sans: {
    key: 'crimson_work_sans',
    displayName: 'Crimson + Work Sans',
    headingFontFamily: "'Crimson Pro', 'Fraunces Variable', serif",
    bodyFontFamily: "'Work Sans', 'Source Sans 3', sans-serif",
    accentFontFamily: "'Crimson Pro', serif",
  },
};

export const DESIGN_THEME_PREVIEW_SURFACES: DesignThemePreviewSurfaceDefinition[] =
  [
    {
      key: 'guest_home',
      displayName: 'Guest home (desktop)',
      previewMode: 'live_shell',
      supportedTokens: [
        'accent',
        'accentStrong',
        'accentSoft',
        'pageBackground',
        'surfacePrimary',
        'surfaceSecondary',
        'sidebarFrom',
        'sidebarTo',
        'inkStrong',
        'inkSoft',
        'headingFontFamily',
        'bodyFontFamily',
        'borderRadius',
        'shadowStyle',
      ],
      supportedAssets: ['logoAssetId', 'markAssetId', 'heroAssetId'],
    },
    {
      key: 'guest_home_mobile',
      displayName: 'Guest home (mobile)',
      previewMode: 'live_shell',
      supportedTokens: [
        'accent',
        'accentStrong',
        'surfacePrimary',
        'surfaceSecondary',
        'inkStrong',
        'inkSoft',
        'headingFontFamily',
        'bodyFontFamily',
        'borderRadius',
        'shadowStyle',
        'componentSizePreference',
        'sectionGapScale',
      ],
      supportedAssets: ['heroAssetId'],
    },
    {
      key: 'guest_schedule',
      displayName: 'Guest schedule',
      previewMode: 'live_shell',
      supportedTokens: [
        'accent',
        'accentStrong',
        'surfacePrimary',
        'surfaceSecondary',
        'inkStrong',
        'inkSoft',
        'headingFontFamily',
        'bodyFontFamily',
        'borderRadius',
        'shadowStyle',
      ],
      supportedAssets: ['markAssetId', 'heroAssetId'],
    },
    {
      key: 'publishing_invitation',
      displayName: 'Invitation publishing',
      previewMode: 'publishing_html',
      supportedTokens: [
        'accent',
        'accentStrong',
        'surfacePrimary',
        'pageBackground',
        'inkStrong',
        'headingFontFamily',
        'bodyFontFamily',
      ],
      supportedAssets: ['logoAssetId', 'heroAssetId'],
    },
    {
      key: 'welcome_sign',
      displayName: 'Welcome sign',
      previewMode: 'publishing_html',
      supportedTokens: [
        'accent',
        'accentStrong',
        'surfacePrimary',
        'pageBackground',
        'inkStrong',
        'headingFontFamily',
        'bodyFontFamily',
      ],
      supportedAssets: ['logoAssetId', 'markAssetId', 'heroAssetId'],
    },
  ];

export const DESIGN_THEME_BASE_PRESETS: Record<
  DesignThemeBaseThemeKey,
  DesignThemeBasePreset
> = {
  citrus_evening_editorial: buildThemePreset({
    key: 'citrus_evening_editorial',
    displayName: 'Elegant Editorial',
    description:
      'Warm editorial wedding styling with amber accents and deep twilight sidebars.',
    styleVariant: 'editorial',
    presentationTone: 'elegant',
    fontKey: 'fraunces_source_sans',
    templates: {
      invitation: 'guest_invitation_classic',
      signage: 'occasion_welcome_sign',
      guestWeb: 'guest_portal_editorial',
    },
    base: {
      accent: '#c76832',
      accentStrong: '#93411f',
      surfacePrimary: '#fff9f2',
      surfaceSecondary: '#fff2e5',
      sidebarFrom: '#2f2137',
      sidebarTo: '#40273f',
      inkStrong: '#1b2436',
      inkSoft: '#5e667a',
      borderRadius: '24px',
      shadowStyle: '0 24px 60px rgba(27, 36, 54, 0.12)',
      primaryBrandColor: '#c76832',
      secondaryBrandColor: '#2f2137',
      neutralPalette: '#1b2436,#5e667a,#ece4db,#ffffff',
      backgroundPalette: '#faf3ea,#f3efe9,#edf4f7',
      borderRadiusFamily: 'rounded-soft',
      elevationFamily: 'editorial-soft',
      fontScale: 'comfortable',
      fontWeightMapping: 'display:700,body:400,cta:600',
      lineHeightPreset: 'relaxed',
      letterSpacingPreset: 'balanced',
      textTransformPreference: 'none',
      spacingScale: 'cozy',
      containerWidthPreference: 'content-1180',
      sectionGapScale: 'comfortable',
      cardDensityPreference: 'comfortable',
      componentSizePreference: 'medium',
      defaultHeroTreatment: 'image-with-gradient',
      imageCornerTreatment: 'soft-rounded',
      imageOverlayStrength: '0.32',
      backgroundImageDimming: '0.24',
      gradientUsage: 'balanced',
      textureNoiseUsage: 'off',
      animationLevel: 'standard',
      hoverIntensity: 'medium',
      transitionDurationProfile: 'standard',
      reducedMotionBehavior: 'respect-system',
    },
  }),
  garden_romance_classic: buildThemePreset({
    key: 'garden_romance_classic',
    displayName: 'Romantic Soft',
    description:
      'Soft floral styling with lighter neutrals for couples who want a guided preset.',
    styleVariant: 'romantic',
    presentationTone: 'classic',
    fontKey: 'playfair_inter',
    templates: {
      invitation: 'save_the_date_editorial',
      signage: 'occasion_welcome_sign',
      guestWeb: 'guest_portal_editorial',
    },
    base: {
      accent: '#9f6f7f',
      accentStrong: '#72495b',
      surfacePrimary: '#fffbf9',
      surfaceSecondary: '#fff4f1',
      sidebarFrom: '#6e4b57',
      sidebarTo: '#88616f',
      inkStrong: '#2d2530',
      inkSoft: '#6a5f68',
      borderRadius: '28px',
      shadowStyle: '0 24px 56px rgba(110, 75, 87, 0.14)',
      primaryBrandColor: '#9f6f7f',
      secondaryBrandColor: '#72495b',
      neutralPalette: '#2d2530,#6a5f68,#f4ece8,#ffffff',
      backgroundPalette: '#fff9f7,#f8f4ef,#edf3ef',
      borderRadiusFamily: 'rounded-romantic',
      elevationFamily: 'romantic-soft',
      fontScale: 'comfortable',
      fontWeightMapping: 'display:700,body:400,cta:600',
      lineHeightPreset: 'airy',
      letterSpacingPreset: 'soft',
      textTransformPreference: 'none',
      spacingScale: 'cozy',
      containerWidthPreference: 'content-1120',
      sectionGapScale: 'comfortable',
      cardDensityPreference: 'comfortable',
      componentSizePreference: 'medium',
      defaultHeroTreatment: 'image-soft-overlay',
      imageCornerTreatment: 'soft-rounded',
      imageOverlayStrength: '0.26',
      backgroundImageDimming: '0.2',
      gradientUsage: 'light',
      textureNoiseUsage: 'off',
      animationLevel: 'subtle',
      hoverIntensity: 'soft',
      transitionDurationProfile: 'gentle',
      reducedMotionBehavior: 'respect-system',
    },
  }),
  midnight_monogram_modern: buildThemePreset({
    key: 'midnight_monogram_modern',
    displayName: 'Modern Minimal',
    description:
      'High-contrast formal styling for evening occasions, signage, and bold publishing.',
    styleVariant: 'modern',
    presentationTone: 'modern',
    fontKey: 'crimson_work_sans',
    templates: {
      invitation: 'guest_qr_card',
      signage: 'occasion_welcome_sign',
      guestWeb: 'guest_portal_editorial',
    },
    base: {
      accent: '#d0a95f',
      accentStrong: '#9d7a37',
      surfacePrimary: '#f8f5ef',
      surfaceSecondary: '#f2ecdf',
      sidebarFrom: '#101722',
      sidebarTo: '#1d2940',
      inkStrong: '#172033',
      inkSoft: '#5d677b',
      borderRadius: '20px',
      shadowStyle: '0 28px 72px rgba(16, 23, 34, 0.22)',
      primaryBrandColor: '#d0a95f',
      secondaryBrandColor: '#101722',
      neutralPalette: '#172033,#5d677b,#ece8df,#ffffff',
      backgroundPalette: '#181824,#1f2638,#edf1f8',
      borderRadiusFamily: 'rounded-minimal',
      elevationFamily: 'modern-deep',
      fontScale: 'compact',
      fontWeightMapping: 'display:700,body:400,cta:600',
      lineHeightPreset: 'balanced',
      letterSpacingPreset: 'tight',
      textTransformPreference: 'uppercase-labels',
      spacingScale: 'balanced',
      containerWidthPreference: 'content-1200',
      sectionGapScale: 'balanced',
      cardDensityPreference: 'compact',
      componentSizePreference: 'medium',
      defaultHeroTreatment: 'image-strong-overlay',
      imageCornerTreatment: 'rounded',
      imageOverlayStrength: '0.38',
      backgroundImageDimming: '0.3',
      gradientUsage: 'strong',
      textureNoiseUsage: 'off',
      animationLevel: 'subtle',
      hoverIntensity: 'medium',
      transitionDurationProfile: 'fast',
      reducedMotionBehavior: 'respect-system',
    },
  }),
  romantic_soft: buildThemePreset({
    key: 'romantic_soft',
    displayName: 'Festive Bright',
    description:
      'Bright and joyful palette that keeps readability guardrails for party-heavy weekends.',
    styleVariant: 'playful',
    presentationTone: 'playful',
    fontKey: 'playfair_inter',
    templates: {
      invitation: 'save_the_date_editorial',
      signage: 'occasion_welcome_sign',
      guestWeb: 'guest_portal_editorial',
    },
    base: {
      accent: '#ff6f4d',
      accentStrong: '#c5462b',
      surfacePrimary: '#fffdf8',
      surfaceSecondary: '#fff3ea',
      sidebarFrom: '#5c2f45',
      sidebarTo: '#753d5a',
      inkStrong: '#2f2235',
      inkSoft: '#6e5e73',
      borderRadius: '30px',
      shadowStyle: '0 24px 56px rgba(197, 70, 43, 0.18)',
      primaryBrandColor: '#ff6f4d',
      secondaryBrandColor: '#5c2f45',
      neutralPalette: '#2f2235,#6e5e73,#f2e8df,#ffffff',
      backgroundPalette: '#fff7f0,#fff1e7,#f2f0f8',
      borderRadiusFamily: 'rounded-playful',
      elevationFamily: 'festive-soft',
      fontScale: 'comfortable',
      fontWeightMapping: 'display:700,body:400,cta:600',
      lineHeightPreset: 'airy',
      letterSpacingPreset: 'balanced',
      textTransformPreference: 'none',
      spacingScale: 'cozy',
      containerWidthPreference: 'content-1120',
      sectionGapScale: 'comfortable',
      cardDensityPreference: 'comfortable',
      componentSizePreference: 'medium',
      defaultHeroTreatment: 'image-with-confetti-gradient',
      imageCornerTreatment: 'soft-rounded',
      imageOverlayStrength: '0.24',
      backgroundImageDimming: '0.18',
      gradientUsage: 'expressive',
      textureNoiseUsage: 'off',
      animationLevel: 'standard',
      hoverIntensity: 'medium',
      transitionDurationProfile: 'standard',
      reducedMotionBehavior: 'respect-system',
    },
  }),
  classic_formal: buildThemePreset({
    key: 'classic_formal',
    displayName: 'Classic Formal',
    description:
      'Traditional formal direction with restrained motion and high-contrast typography.',
    styleVariant: 'formal',
    presentationTone: 'formal',
    fontKey: 'crimson_work_sans',
    templates: {
      invitation: 'guest_invitation_classic',
      signage: 'occasion_welcome_sign',
      guestWeb: 'guest_portal_editorial',
    },
    base: {
      accent: '#3f506b',
      accentStrong: '#2b3950',
      surfacePrimary: '#fcfcfb',
      surfaceSecondary: '#f3f4f6',
      sidebarFrom: '#1f2a3a',
      sidebarTo: '#2d3b4f',
      inkStrong: '#162033',
      inkSoft: '#596375',
      borderRadius: '16px',
      shadowStyle: '0 18px 42px rgba(22, 32, 51, 0.14)',
      primaryBrandColor: '#3f506b',
      secondaryBrandColor: '#1f2a3a',
      neutralPalette: '#162033,#596375,#e4e8ef,#ffffff',
      backgroundPalette: '#f5f7fb,#eef1f6,#e6e9f0',
      borderRadiusFamily: 'rounded-classic',
      elevationFamily: 'formal-crisp',
      fontScale: 'compact',
      fontWeightMapping: 'display:700,body:400,cta:600',
      lineHeightPreset: 'balanced',
      letterSpacingPreset: 'tight',
      textTransformPreference: 'smallcaps-headings',
      spacingScale: 'balanced',
      containerWidthPreference: 'content-1180',
      sectionGapScale: 'balanced',
      cardDensityPreference: 'compact',
      componentSizePreference: 'medium',
      defaultHeroTreatment: 'image-structured-overlay',
      imageCornerTreatment: 'rounded',
      imageOverlayStrength: '0.34',
      backgroundImageDimming: '0.28',
      gradientUsage: 'light',
      textureNoiseUsage: 'off',
      animationLevel: 'minimal',
      hoverIntensity: 'soft',
      transitionDurationProfile: 'gentle',
      reducedMotionBehavior: 'respect-system',
    },
  }),
};

function normalizeHexColor(value: string) {
  return /^#[0-9a-f]{6}$/i.test(value.trim())
    ? value.trim().toLowerCase()
    : null;
}

function deriveAccentSoft(hex: string) {
  const red = Number.parseInt(hex.slice(1, 3), 16);
  const green = Number.parseInt(hex.slice(3, 5), 16);
  const blue = Number.parseInt(hex.slice(5, 7), 16);

  return `rgba(${red}, ${green}, ${blue}, 0.16)`;
}

function derivePageBackground(input: {
  accent: string;
  surfaceSecondary: string;
  backgroundPalette: string;
}) {
  const accent = normalizeHexColor(input.accent) ?? '#c76832';
  const secondary = normalizeHexColor(input.surfaceSecondary) ?? '#fff7ef';
  const [firstBackground] = input.backgroundPalette
    .split(',')
    .map((item) => item.trim());
  const base = normalizeHexColor(firstBackground) ?? secondary;
  const red = Number.parseInt(accent.slice(1, 3), 16);
  const green = Number.parseInt(accent.slice(3, 5), 16);
  const blue = Number.parseInt(accent.slice(5, 7), 16);

  return `radial-gradient(circle at top, rgba(${red}, ${green}, ${blue}, 0.18), transparent 32%), linear-gradient(180deg, ${base} 0%, #f5f1eb 56%, #edf3f8 100%)`;
}

function parseNumber(value: string, fallback: number) {
  const parsed = Number.parseFloat(value);

  return Number.isFinite(parsed) ? parsed : fallback;
}

function contrastRatio(hexA: string, hexB: string) {
  const normalize = (hex: string) => {
    const red = Number.parseInt(hex.slice(1, 3), 16) / 255;
    const green = Number.parseInt(hex.slice(3, 5), 16) / 255;
    const blue = Number.parseInt(hex.slice(5, 7), 16) / 255;
    const channel = (value: number) =>
      value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;

    return (
      0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue)
    );
  };

  const lumA = normalize(hexA);
  const lumB = normalize(hexB);
  const lightest = Math.max(lumA, lumB);
  const darkest = Math.min(lumA, lumB);

  return (lightest + 0.05) / (darkest + 0.05);
}

function buildThemePreset(input: {
  key: DesignThemeBaseThemeKey;
  displayName: string;
  description: string;
  styleVariant: DesignThemeStyleVariant;
  presentationTone: DesignThemePresentationTone;
  fontKey: DesignThemeFontKey;
  templates: DesignThemeTemplates;
  base: {
    accent: string;
    accentStrong: string;
    surfacePrimary: string;
    surfaceSecondary: string;
    sidebarFrom: string;
    sidebarTo: string;
    inkStrong: string;
    inkSoft: string;
    borderRadius: string;
    shadowStyle: string;
    primaryBrandColor: string;
    secondaryBrandColor: string;
    neutralPalette: string;
    backgroundPalette: string;
    borderRadiusFamily: string;
    elevationFamily: string;
    fontScale: string;
    fontWeightMapping: string;
    lineHeightPreset: string;
    letterSpacingPreset: string;
    textTransformPreference: string;
    spacingScale: string;
    containerWidthPreference: string;
    sectionGapScale: string;
    cardDensityPreference: string;
    componentSizePreference: string;
    defaultHeroTreatment: string;
    imageCornerTreatment: string;
    imageOverlayStrength: string;
    backgroundImageDimming: string;
    gradientUsage: string;
    textureNoiseUsage: string;
    animationLevel: string;
    hoverIntensity: string;
    transitionDurationProfile: string;
    reducedMotionBehavior: string;
  };
}): DesignThemeBasePreset {
  const font = DESIGN_THEME_FONT_CHOICES[input.fontKey];
  const accentSoft = deriveAccentSoft(input.base.accent);
  const pageBackground = derivePageBackground({
    accent: input.base.accent,
    surfaceSecondary: input.base.surfaceSecondary,
    backgroundPalette: input.base.backgroundPalette,
  });

  return {
    key: input.key,
    displayName: input.displayName,
    description: input.description,
    styleVariant: input.styleVariant,
    presentationTone: input.presentationTone,
    fontKey: input.fontKey,
    templates: input.templates,
    tokens: {
      ...input.base,
      accentSoft,
      pageBackground,
      headingFontFamily: font.headingFontFamily,
      bodyFontFamily: font.bodyFontFamily,
      accentFontFamily: font.accentFontFamily,
      presentationTone: input.presentationTone,
    },
  };
}

export function resolveDesignThemeBasePreset(
  key: DesignThemeBaseThemeKey,
): DesignThemeBasePreset {
  return DESIGN_THEME_BASE_PRESETS[key];
}

export function isSupportedDesignThemeFontKey(
  value: string,
): value is DesignThemeFontKey {
  return value in DESIGN_THEME_FONT_CHOICES;
}

export function buildDesignThemeTokens(input: {
  baseThemeKey: DesignThemeBaseThemeKey;
  tokenOverrides?: DesignThemeTokenOverrides;
}): DesignThemeTokens {
  const preset = resolveDesignThemeBasePreset(input.baseThemeKey);
  const overrides = input.tokenOverrides ?? {};
  const presetPrimaryBrandColor =
    preset.tokens.primaryBrandColor ?? preset.tokens.accent;
  const presetSecondaryBrandColor =
    preset.tokens.secondaryBrandColor ?? preset.tokens.accentStrong;
  const presetNeutralPalette =
    preset.tokens.neutralPalette ??
    `${preset.tokens.inkStrong},${preset.tokens.inkSoft}`;
  const presetBackgroundPalette =
    preset.tokens.backgroundPalette ??
    `${preset.tokens.surfaceSecondary},${preset.tokens.surfacePrimary}`;
  const presetBorderRadiusFamily =
    preset.tokens.borderRadiusFamily ?? 'rounded';
  const presetElevationFamily = preset.tokens.elevationFamily ?? 'default';
  const presetAccentFontFamily =
    preset.tokens.accentFontFamily ?? preset.tokens.headingFontFamily;
  const presetFontScale = preset.tokens.fontScale ?? 'comfortable';
  const presetFontWeightMapping =
    preset.tokens.fontWeightMapping ?? 'display:700,body:400,cta:600';
  const presetLineHeightPreset = preset.tokens.lineHeightPreset ?? 'balanced';
  const presetLetterSpacingPreset =
    preset.tokens.letterSpacingPreset ?? 'balanced';
  const presetTextTransformPreference =
    preset.tokens.textTransformPreference ?? 'none';
  const presetSpacingScale = preset.tokens.spacingScale ?? 'balanced';
  const presetContainerWidthPreference =
    preset.tokens.containerWidthPreference ?? 'content-1120';
  const presetSectionGapScale = preset.tokens.sectionGapScale ?? 'balanced';
  const presetCardDensityPreference =
    preset.tokens.cardDensityPreference ?? 'comfortable';
  const presetComponentSizePreference =
    preset.tokens.componentSizePreference ?? 'medium';
  const presetDefaultHeroTreatment =
    preset.tokens.defaultHeroTreatment ?? 'image-with-gradient';
  const presetImageCornerTreatment =
    preset.tokens.imageCornerTreatment ?? 'rounded';
  const presetImageOverlayStrength =
    preset.tokens.imageOverlayStrength ?? '0.3';
  const presetBackgroundImageDimming =
    preset.tokens.backgroundImageDimming ?? '0.25';
  const presetGradientUsage = preset.tokens.gradientUsage ?? 'balanced';
  const presetTextureNoiseUsage = preset.tokens.textureNoiseUsage ?? 'off';
  const presetAnimationLevel = preset.tokens.animationLevel ?? 'standard';
  const presetHoverIntensity = preset.tokens.hoverIntensity ?? 'medium';
  const presetTransitionDurationProfile =
    preset.tokens.transitionDurationProfile ?? 'standard';
  const presetReducedMotionBehavior =
    preset.tokens.reducedMotionBehavior ?? 'respect-system';
  const presetPresentationTone = preset.tokens.presentationTone ?? 'elegant';
  const accent = normalizeHexColor(overrides.accent ?? preset.tokens.accent);
  const accentStrong = normalizeHexColor(
    overrides.accentStrong ?? preset.tokens.accentStrong,
  );
  const surfacePrimary = normalizeHexColor(
    overrides.surfacePrimary ?? preset.tokens.surfacePrimary,
  );
  const surfaceSecondary = normalizeHexColor(
    overrides.surfaceSecondary ?? preset.tokens.surfaceSecondary,
  );
  const sidebarFrom = normalizeHexColor(
    overrides.sidebarFrom ?? preset.tokens.sidebarFrom,
  );
  const sidebarTo = normalizeHexColor(
    overrides.sidebarTo ?? preset.tokens.sidebarTo,
  );
  const inkStrong = normalizeHexColor(
    overrides.inkStrong ?? preset.tokens.inkStrong,
  );
  const inkSoft = normalizeHexColor(overrides.inkSoft ?? preset.tokens.inkSoft);
  const primaryBrandColor = normalizeHexColor(
    overrides.primaryBrandColor ?? presetPrimaryBrandColor,
  );
  const secondaryBrandColor = normalizeHexColor(
    overrides.secondaryBrandColor ?? presetSecondaryBrandColor,
  );

  const neutralPalette =
    overrides.neutralPalette?.trim() || presetNeutralPalette;
  const backgroundPalette =
    overrides.backgroundPalette?.trim() || presetBackgroundPalette;

  return {
    ...preset.tokens,
    accent: accent ?? preset.tokens.accent,
    accentStrong: accentStrong ?? preset.tokens.accentStrong,
    accentSoft: deriveAccentSoft(accent ?? preset.tokens.accent),
    pageBackground: derivePageBackground({
      accent: accent ?? preset.tokens.accent,
      surfaceSecondary: surfaceSecondary ?? preset.tokens.surfaceSecondary,
      backgroundPalette,
    }),
    surfacePrimary: surfacePrimary ?? preset.tokens.surfacePrimary,
    surfaceSecondary: surfaceSecondary ?? preset.tokens.surfaceSecondary,
    sidebarFrom: sidebarFrom ?? preset.tokens.sidebarFrom,
    sidebarTo: sidebarTo ?? preset.tokens.sidebarTo,
    inkStrong: inkStrong ?? preset.tokens.inkStrong,
    inkSoft: inkSoft ?? preset.tokens.inkSoft,
    headingFontFamily:
      overrides.headingFontFamily ?? preset.tokens.headingFontFamily,
    bodyFontFamily: overrides.bodyFontFamily ?? preset.tokens.bodyFontFamily,
    primaryBrandColor: primaryBrandColor ?? presetPrimaryBrandColor,
    secondaryBrandColor: secondaryBrandColor ?? presetSecondaryBrandColor,
    neutralPalette,
    backgroundPalette,
    borderRadiusFamily:
      overrides.borderRadiusFamily ?? presetBorderRadiusFamily,
    elevationFamily: overrides.elevationFamily ?? presetElevationFamily,
    accentFontFamily: overrides.accentFontFamily ?? presetAccentFontFamily,
    fontScale: overrides.fontScale ?? presetFontScale,
    fontWeightMapping: overrides.fontWeightMapping ?? presetFontWeightMapping,
    lineHeightPreset: overrides.lineHeightPreset ?? presetLineHeightPreset,
    letterSpacingPreset:
      overrides.letterSpacingPreset ?? presetLetterSpacingPreset,
    textTransformPreference:
      overrides.textTransformPreference ?? presetTextTransformPreference,
    spacingScale: overrides.spacingScale ?? presetSpacingScale,
    containerWidthPreference:
      overrides.containerWidthPreference ?? presetContainerWidthPreference,
    sectionGapScale: overrides.sectionGapScale ?? presetSectionGapScale,
    cardDensityPreference:
      overrides.cardDensityPreference ?? presetCardDensityPreference,
    componentSizePreference:
      overrides.componentSizePreference ?? presetComponentSizePreference,
    defaultHeroTreatment:
      overrides.defaultHeroTreatment ?? presetDefaultHeroTreatment,
    imageCornerTreatment:
      overrides.imageCornerTreatment ?? presetImageCornerTreatment,
    imageOverlayStrength:
      overrides.imageOverlayStrength ?? presetImageOverlayStrength,
    backgroundImageDimming:
      overrides.backgroundImageDimming ?? presetBackgroundImageDimming,
    gradientUsage: overrides.gradientUsage ?? presetGradientUsage,
    textureNoiseUsage: overrides.textureNoiseUsage ?? presetTextureNoiseUsage,
    animationLevel: overrides.animationLevel ?? presetAnimationLevel,
    hoverIntensity: overrides.hoverIntensity ?? presetHoverIntensity,
    transitionDurationProfile:
      overrides.transitionDurationProfile ?? presetTransitionDurationProfile,
    reducedMotionBehavior:
      overrides.reducedMotionBehavior ?? presetReducedMotionBehavior,
    presentationTone: overrides.presentationTone ?? presetPresentationTone,
  };
}

export function buildDesignThemeModel(theme: {
  baseThemeKey: DesignThemeBaseThemeKey;
  tokenOverrides?: DesignThemeTokenOverrides;
}): DesignThemeModel {
  const tokens = buildDesignThemeTokens(theme);

  return {
    foundation: {
      occasionTitleDefaultStyle: tokens.presentationTone ?? 'elegant',
      primaryBrandColor: tokens.primaryBrandColor ?? tokens.accent,
      secondaryBrandColor: tokens.secondaryBrandColor ?? tokens.accentStrong,
      accentColor: tokens.accent,
      neutralPalette:
        tokens.neutralPalette ?? `${tokens.inkStrong},${tokens.inkSoft}`,
      backgroundPalette:
        tokens.backgroundPalette ??
        `${tokens.surfaceSecondary},${tokens.surfacePrimary}`,
      borderRadiusFamily: tokens.borderRadiusFamily ?? 'rounded',
      elevationFamily: tokens.elevationFamily ?? 'default',
    },
    typography: {
      headingFontFamily: tokens.headingFontFamily,
      bodyFontFamily: tokens.bodyFontFamily,
      accentDecorativeFontFamily:
        tokens.accentFontFamily ?? tokens.headingFontFamily,
      fontScale: tokens.fontScale ?? 'comfortable',
      fontWeightMapping:
        tokens.fontWeightMapping ?? 'display:700,body:400,cta:600',
      lineHeightPreset: tokens.lineHeightPreset ?? 'balanced',
      letterSpacingPreset: tokens.letterSpacingPreset ?? 'balanced',
      textTransformPreference: tokens.textTransformPreference ?? 'none',
    },
    shapeAndSpacing: {
      borderRadiusScale: tokens.borderRadius,
      spacingScale: tokens.spacingScale ?? 'balanced',
      containerWidthPreference:
        tokens.containerWidthPreference ?? 'content-1120',
      sectionGapScale: tokens.sectionGapScale ?? 'balanced',
      cardDensityPreference: tokens.cardDensityPreference ?? 'comfortable',
      componentSizePreference: tokens.componentSizePreference ?? 'medium',
    },
    mediaBehavior: {
      defaultHeroTreatment:
        tokens.defaultHeroTreatment ?? 'image-with-gradient',
      imageCornerTreatment: tokens.imageCornerTreatment ?? 'rounded',
      imageOverlayStrength: tokens.imageOverlayStrength ?? '0.3',
      backgroundImageDimming: tokens.backgroundImageDimming ?? '0.25',
      gradientUsage: tokens.gradientUsage ?? 'balanced',
      textureNoiseUsage: tokens.textureNoiseUsage ?? 'off',
    },
    motionAndInteraction: {
      animationLevel: tokens.animationLevel ?? 'standard',
      hoverIntensity: tokens.hoverIntensity ?? 'medium',
      transitionDurationProfile: tokens.transitionDurationProfile ?? 'standard',
      reducedMotionBehavior: tokens.reducedMotionBehavior ?? 'respect-system',
    },
    presentationTone: tokens.presentationTone ?? 'elegant',
  };
}

export function buildGuestTheme(input: {
  occasionId: OccasionId;
  themeId: DesignThemeId;
  themeName: string;
  version: string | number;
  baseThemeKey: DesignThemeBaseThemeKey;
  styleVariant: DesignThemeStyleVariant;
  presentationTone: DesignThemePresentationTone;
  tokens: DesignThemeTokens;
  assets?: Partial<{
    logo: GuestThemeAssetReference;
    mark: GuestThemeAssetReference;
    hero: GuestThemeAssetReference;
    decorative: GuestThemeAssetReference;
  }>;
  templates: DesignThemeTemplates;
}): GuestTheme {
  const border =
    input.tokens.surfaceSecondary === input.tokens.inkStrong
      ? input.tokens.surfaceSecondary
      : `color-mix(in srgb, ${input.tokens.surfaceSecondary} 72%, ${input.tokens.inkStrong})`;
  const borderStrong =
    input.tokens.surfaceSecondary === input.tokens.inkStrong
      ? input.tokens.surfaceSecondary
      : `color-mix(in srgb, ${input.tokens.surfaceSecondary} 56%, ${input.tokens.inkStrong})`;

  return {
    occasionId: input.occasionId,
    themeId: input.themeId,
    themeName: input.themeName,
    version: String(input.version),
    baseThemeKey: input.baseThemeKey,
    styleVariant: input.styleVariant,
    presentationTone: input.presentationTone,
    colors: {
      primary: input.tokens.primaryBrandColor ?? input.tokens.accent,
      secondary: input.tokens.secondaryBrandColor ?? input.tokens.accentStrong,
      accent: input.tokens.accent,
      accentStrong: input.tokens.accentStrong,
      accentSoft: input.tokens.accentSoft,
      background: input.tokens.pageBackground,
      surface: input.tokens.surfacePrimary,
      surfaceSecondary: input.tokens.surfaceSecondary,
      text: input.tokens.inkStrong,
      mutedText: input.tokens.inkSoft,
      sidebarFrom: input.tokens.sidebarFrom,
      sidebarTo: input.tokens.sidebarTo,
      border,
      borderStrong,
    },
    typography: {
      headingFontFamily: input.tokens.headingFontFamily,
      bodyFontFamily: input.tokens.bodyFontFamily,
      accentFontFamily:
        input.tokens.accentFontFamily ?? input.tokens.headingFontFamily,
      fontScale: input.tokens.fontScale ?? 'comfortable',
      lineHeightPreset: input.tokens.lineHeightPreset ?? 'balanced',
      letterSpacingPreset: input.tokens.letterSpacingPreset ?? 'balanced',
      textTransformPreference: input.tokens.textTransformPreference ?? 'none',
    },
    layout: {
      borderRadius: input.tokens.borderRadius,
      borderRadiusFamily: input.tokens.borderRadiusFamily ?? 'rounded',
      containerWidthPreference:
        input.tokens.containerWidthPreference ?? 'content-1120',
      spacingScale: input.tokens.spacingScale ?? 'balanced',
      sectionGapScale: input.tokens.sectionGapScale ?? 'balanced',
      cardDensityPreference:
        input.tokens.cardDensityPreference ?? 'comfortable',
      componentSizePreference: input.tokens.componentSizePreference ?? 'medium',
    },
    motion: {
      animationLevel: input.tokens.animationLevel ?? 'standard',
      hoverIntensity: input.tokens.hoverIntensity ?? 'medium',
      transitionDurationProfile:
        input.tokens.transitionDurationProfile ?? 'standard',
      reducedMotionBehavior:
        input.tokens.reducedMotionBehavior ?? 'respect-system',
    },
    media: {
      defaultHeroTreatment:
        input.tokens.defaultHeroTreatment ?? 'image-with-gradient',
      imageCornerTreatment: input.tokens.imageCornerTreatment ?? 'rounded',
      imageOverlayStrength: input.tokens.imageOverlayStrength ?? '0.3',
      backgroundImageDimming: input.tokens.backgroundImageDimming ?? '0.25',
      gradientUsage: input.tokens.gradientUsage ?? 'balanced',
      textureNoiseUsage: input.tokens.textureNoiseUsage ?? 'off',
    },
    assets: {
      logo: input.assets?.logo ?? {
        assetId: null,
        url: null,
      },
      mark: input.assets?.mark ?? {
        assetId: null,
        url: null,
      },
      hero: input.assets?.hero ?? {
        assetId: null,
        url: null,
      },
      decorative: input.assets?.decorative ?? {
        assetId: null,
        url: null,
      },
    },
    components: {
      button: {
        radius: input.tokens.borderRadius,
        shadow: input.tokens.shadowStyle,
        hoverIntensity: input.tokens.hoverIntensity ?? 'medium',
        textTransformPreference: input.tokens.textTransformPreference ?? 'none',
      },
      card: {
        radius: input.tokens.borderRadius,
        shadow: input.tokens.shadowStyle,
        density: input.tokens.cardDensityPreference ?? 'comfortable',
        background: input.tokens.surfacePrimary,
        backgroundSecondary: input.tokens.surfaceSecondary,
      },
    },
    templates: input.templates,
    tokens: input.tokens,
  };
}

export function createDefaultDesignThemeIdentity(input: {
  baseThemeKey: DesignThemeBaseThemeKey;
  updatedAt: string;
  isCustomTheme?: boolean;
  migratedFromLegacyStudio?: boolean;
  internalThemeVersion?: number;
}): DesignThemeIdentity {
  return {
    internalThemeVersion: input.internalThemeVersion ?? 1,
    presetSource: input.baseThemeKey,
    isCustomTheme: input.isCustomTheme ?? false,
    migratedFromLegacyStudio: input.migratedFromLegacyStudio ?? false,
    lastUpdatedAt: input.updatedAt,
  };
}

function applyTokenLayer(
  base: DesignThemeTokens,
  layer?: Partial<DesignThemeTokens> | null,
): DesignThemeTokens {
  if (!layer) {
    return base;
  }

  return {
    ...base,
    ...Object.fromEntries(
      Object.entries(layer).filter(([, value]) => value !== undefined),
    ),
  };
}

export function resolveDesignThemeTokensWithPrecedence(input: {
  baseThemeKey: DesignThemeBaseThemeKey;
  occasionOverrides?: DesignThemeTokenOverrides;
  eventVisualOverride?: DesignThemeEventVisualOverrideRecord | null;
  sectionOverrides?: Partial<DesignThemeTokens> | null;
}) {
  const systemDefaults = buildDesignThemeTokens({
    baseThemeKey: input.baseThemeKey,
  });
  const occasionTheme = buildDesignThemeTokens({
    baseThemeKey: input.baseThemeKey,
    tokenOverrides: input.occasionOverrides,
  });
  const eventLayer: Partial<DesignThemeTokens> = {};

  if (input.eventVisualOverride?.eventAccentColor) {
    eventLayer.accent = input.eventVisualOverride.eventAccentColor;
    eventLayer.accentStrong = input.eventVisualOverride.eventAccentColor;
    eventLayer.accentSoft = deriveAccentSoft(
      input.eventVisualOverride.eventAccentColor,
    );
  }

  const withEvent = applyTokenLayer(occasionTheme, eventLayer);
  const resolved = applyTokenLayer(withEvent, input.sectionOverrides);

  return {
    systemDefaults,
    occasionTheme,
    eventTheme: withEvent,
    resolved,
    precedence: [
      'system_defaults',
      'occasion_theme',
      'event_visual_override',
      'guest_section_override',
    ] as const,
  };
}

export function sanitizeDesignThemeTokenOverrides(
  overrides: Partial<Record<keyof DesignThemeTokenOverrides, string | null>>,
): DesignThemeTokenOverrides {
  const sanitized: DesignThemeTokenOverrides = {};

  for (const [key, value] of Object.entries(overrides) as Array<
    [keyof DesignThemeTokenOverrides, string | null]
  >) {
    if (typeof value !== 'string') {
      continue;
    }

    const trimmed = value.trim();

    if (!trimmed) {
      continue;
    }

    if (
      [
        'headingFontFamily',
        'bodyFontFamily',
        'borderRadiusFamily',
        'elevationFamily',
        'accentFontFamily',
        'fontScale',
        'fontWeightMapping',
        'lineHeightPreset',
        'letterSpacingPreset',
        'textTransformPreference',
        'spacingScale',
        'containerWidthPreference',
        'sectionGapScale',
        'cardDensityPreference',
        'componentSizePreference',
        'defaultHeroTreatment',
        'imageCornerTreatment',
        'imageOverlayStrength',
        'backgroundImageDimming',
        'gradientUsage',
        'textureNoiseUsage',
        'animationLevel',
        'hoverIntensity',
        'transitionDurationProfile',
        'reducedMotionBehavior',
        'neutralPalette',
        'backgroundPalette',
        'presentationTone',
      ].includes(key)
    ) {
      (sanitized as Record<string, string>)[key] = trimmed;
      continue;
    }

    const normalized = normalizeHexColor(trimmed);

    if (normalized) {
      (sanitized as Record<string, string>)[key] = normalized;
    }
  }

  return sanitized;
}

export function validateDesignThemeRecord(theme: {
  baseThemeKey: DesignThemeBaseThemeKey;
  tokenOverrides?: DesignThemeTokenOverrides;
}): { isValid: boolean; issues: string[]; warnings: string[] } {
  const issues: string[] = [];
  const warnings: string[] = [];
  const overrides = theme.tokenOverrides ?? {};
  const tokens = buildDesignThemeTokens(theme);

  for (const [key, value] of Object.entries(overrides)) {
    if (
      [
        'headingFontFamily',
        'bodyFontFamily',
        'borderRadiusFamily',
        'elevationFamily',
        'accentFontFamily',
        'fontScale',
        'fontWeightMapping',
        'lineHeightPreset',
        'letterSpacingPreset',
        'textTransformPreference',
        'spacingScale',
        'containerWidthPreference',
        'sectionGapScale',
        'cardDensityPreference',
        'componentSizePreference',
        'defaultHeroTreatment',
        'imageCornerTreatment',
        'imageOverlayStrength',
        'backgroundImageDimming',
        'gradientUsage',
        'textureNoiseUsage',
        'animationLevel',
        'hoverIntensity',
        'transitionDurationProfile',
        'reducedMotionBehavior',
        'neutralPalette',
        'backgroundPalette',
        'presentationTone',
      ].includes(key)
    ) {
      continue;
    }

    if (value && !normalizeHexColor(value)) {
      issues.push(`${key} must be a 6-digit hex color.`);
    }
  }

  const contrast = contrastRatio(tokens.inkStrong, tokens.surfacePrimary);
  if (contrast < 4.5) {
    issues.push(
      'Primary text contrast is below WCAG AA (4.5:1) for body content.',
    );
  } else if (contrast < 6) {
    warnings.push(
      'Primary text contrast is valid but close to the minimum threshold.',
    );
  }

  const accentContrast = contrastRatio(
    tokens.accentStrong,
    tokens.surfacePrimary,
  );
  if (accentContrast < 3) {
    warnings.push(
      'Accent contrast on primary surface is low for buttons and labels.',
    );
  }

  if (tokens.headingFontFamily === tokens.bodyFontFamily) {
    warnings.push(
      'Heading and body fonts are identical; consider separate families for clearer hierarchy.',
    );
  }

  if (
    tokens.reducedMotionBehavior !== 'respect-system' &&
    tokens.animationLevel !== 'minimal'
  ) {
    warnings.push(
      'Reduced motion behavior should usually respect system preferences when animation is not minimal.',
    );
  }

  const overlayStrength = parseNumber(
    tokens.imageOverlayStrength ?? '0.3',
    0.3,
  );
  if (overlayStrength < 0.15) {
    warnings.push(
      'Hero overlay strength is very low and may reduce text readability on images.',
    );
  }

  if (tokens.fontScale === 'compact' && tokens.lineHeightPreset === 'tight') {
    warnings.push(
      'Compact font scale plus tight line-height can hurt readability on mobile.',
    );
  }

  if (
    tokens.presentationTone === 'playful' &&
    tokens.animationLevel === 'minimal'
  ) {
    warnings.push(
      'Playful tone with minimal motion can feel visually inconsistent.',
    );
  }

  return {
    isValid: issues.length === 0,
    issues,
    warnings,
  };
}
